import ContinueButton from '@components/continueButton/ContinueButton';
import { InputBox } from '@imprivata-cloud/components';
import { Alert } from '@imprivata-cloud/design-system';
import { Form } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

enum FieldNames {
  PASSWORD = 'password',
}

interface FormValues {
  [FieldNames.PASSWORD]: string;
}

const TRANSLATION = 'self-service.epcs-setup.individual.password';

interface EpcsIndividualPasswordProps {
  /** True while the password is being verified, so Continue cannot submit it twice. */
  busy: boolean;
  rejections?: number;
  onSubmit: (password: string) => void;
  cancelFlow: () => void;
}

const EpcsIndividualPassword = ({
  busy,
  rejections = 0,
  onSubmit,
  cancelFlow,
}: EpcsIndividualPasswordProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const password = Form.useWatch(FieldNames.PASSWORD, form);
  // Each rejection raises the count, so a banner closed for one rejection returns for the next.
  const [dismissedRejections, setDismissedRejections] = useState(0);
  const showRejectedBanner = rejections > dismissedRejections;

  // A password that was turned down is not worth keeping, so the field starts empty again.
  useEffect(() => {
    if (rejections > 0) {
      form.setFieldValue(FieldNames.PASSWORD, '');
    }
  }, [rejections, form]);

  return (
    <Form<FormValues>
      className="continue-layout"
      form={form}
      noValidate
      onFinish={values => onSubmit(values[FieldNames.PASSWORD])}
    >
      <div>
        {showRejectedBanner && (
          <Alert
            status="warning"
            title={t(`${TRANSLATION}.credentials-rejected`)}
            onClose={() => setDismissedRejections(rejections)}
            className="password-banner"
          />
        )}
        <h2 className="title">{t(`${TRANSLATION}.title`)}</h2>
        <p>{t(`${TRANSLATION}.instructions`)}</p>
        <Form.Item
          name={FieldNames.PASSWORD}
          rules={[{ required: true, message: t(`${TRANSLATION}.error.password-required`) }]}
        >
          {/* The title already names the field, so the design leaves it unlabelled. */}
          <InputBox autoFocus type="password" aria-label="password" />
        </Form.Item>
      </div>

      <div>
        <ContinueButton
          htmlType="submit"
          disabled={!password}
          loading={busy}
          secondaryButton={{ label: t('common.do-later'), onClick: cancelFlow }}
        >
          {t('common.continue-button')}
        </ContinueButton>
      </div>
    </Form>
  );
};

export default EpcsIndividualPassword;
